import type { CardInfo } from "@domain/dtos/models/card.models";
import { Card } from "@boundary/components/card";
import { useCreditCard } from "@boundary/_hooks";
import { ChevronRight } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { HandleableError } from "@shared/utils/errors/handleableError";
import styles from "./selectCard.module.scss";

// УБРАНО: CARD_REFETCH_INTERVAL - больше не используется автоматическое обновление

interface CardItemProps {
  item: CardInfo | null;
  disabled?: boolean;
  onClick: () => void;
}

function CardItem({ item, disabled, onClick }: CardItemProps) {
  const isSelected = item?.is_chosen ?? false;
  const displayText = item?.pan ?? (disabled ? "Загрузка..." : "Ввести данные карты");
  const showChevron = !item;

  return (
    <Card
      asChild
      size="sm"
      state={isSelected ? "selected" : undefined}
      className={styles.cardButton}
    >
      <button
        disabled={isSelected || disabled}
        onClick={() => {
          console.log(`[Button Click] SelectCard: "Выбрать карту" (card_text: ${displayText}, disabled: ${isSelected || disabled})`);
          onClick();
        }}
      >
        <img src="/assets/billing/card.png" alt="" className={styles.cardIcon} />
        {displayText}
        {showChevron && <ChevronRight className={styles.cardChevron} />}
      </button>
    </Card>
  );
}

interface SelectCardProps {
  onCardSelected?: (card: CardInfo) => void;
  hideNewCard?: boolean;
  cards?: CardInfo[];
}

function useCardRefetch() {
  const { fetchCards } = useCreditCard();

  useEffect(() => {
    // ИЗМЕНЕНО: загружаем карты только один раз при монтировании компонента
    // Убрали автоматическое обновление по таймеру - карты обновляются только при действиях пользователя
    const fetchWithErrorHandling = async () => {
      try {
        await fetchCards();
      } catch (e) {
        if (e instanceof HandleableError) {
          e.handle();
          console.warn("Failed to fetch cards:", e);
        } else {
          console.error("Unexpected error fetching cards:", e);
        }
      }
    };

    void fetchWithErrorHandling();
    // УБРАНО: setInterval для автоматического обновления
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function SelectCard({ onCardSelected, hideNewCard, cards }: SelectCardProps) {
  const { savedCards, addCard } = useCreditCard();
  const [isAddCardDisabled, setIsAddCardDisabled] = useState(false);

  useCardRefetch();
  
  // Если переданы карты через props - используем их, иначе из store
  const displayCards = cards ?? savedCards;

  const handleAddCard = useCallback(async () => {
    if (isAddCardDisabled) return;

    setIsAddCardDisabled(true);

    try {
      await addCard();
    } catch (e) {
      if (e instanceof HandleableError) {
        e.handle();
        e.showError();
      } else {
        console.error("Unexpected error adding card:", e);
      }
    } finally {
      setTimeout(() => {
        setIsAddCardDisabled(false);
      }, 1000);
    }
  }, [isAddCardDisabled, addCard]);

  const handleCardClick = useCallback(
    (card: CardInfo) => {
      onCardSelected?.(card);
    },
    [onCardSelected]
  );

  return (
    <ul className={styles.cardList}>
      {displayCards.map((card) => (
        <li key={card.id} className={styles.cardListItem}>
          <CardItem item={card} onClick={() => handleCardClick(card)} />
        </li>
      ))}
      {!hideNewCard && (
        <li className={styles.cardListItem}>
          <CardItem
            item={null}
            onClick={() => {
              console.log(`[Button Click] SelectCard: "Добавить новую карту"`);
              handleAddCard();
            }}
            disabled={isAddCardDisabled}
          />
        </li>
      )}
    </ul>
  );
}

